import { Link } from 'react-router-dom';
import type { MarketplaceListing } from '../lib/marketplace-api';
import { formatPrice, categoryLabel, CONDITION_LABELS } from '../lib/marketplace-categories';

const CONDITION_STYLES: Record<string, string> = {
  new: 'bg-emerald-500/90 text-white',
  used: 'bg-slate-600/90 text-white',
  refurbished: 'bg-amber-500/90 text-white',
  parts: 'bg-purple-500/90 text-white',
};

export default function MarketplaceCard({ listing }: { listing: MarketplaceListing }) {
  const image = listing.images?.[0];

  return (
    <Link
      to={`/marketplace/${listing.slug}`}
      className="group bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-lg overflow-hidden hover:border-accent/40 hover:shadow-lg hover:shadow-accent/5 transition-all flex flex-col"
    >
      <div className="relative aspect-[4/3] bg-surface-light dark:bg-surface overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={listing.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-text-muted">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
          </div>
        )}
        <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[0.65rem] font-bold uppercase tracking-wide ${CONDITION_STYLES[listing.condition] || CONDITION_STYLES.used}`}>
          {CONDITION_LABELS[listing.condition] || listing.condition}
        </span>
        {listing.featured && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded text-[0.65rem] font-bold uppercase tracking-wide bg-accent text-white">
            Featured
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="text-[0.65rem] font-semibold uppercase tracking-wider text-accent mb-1">
          {categoryLabel(listing.category)}
        </div>
        <h3 className="font-semibold text-sm leading-snug mb-2 line-clamp-2 group-hover:text-accent transition-colors">
          {listing.title}
        </h3>
        <div className="mt-auto flex items-end justify-between gap-2">
          <span className="font-display font-bold text-base text-text-primary-light dark:text-text-primary">
            {formatPrice(listing.price, listing.price_type)}
          </span>
          <span className="text-xs text-text-muted truncate">
            {listing.city ? `${listing.city}, ` : ''}{listing.region}
          </span>
        </div>
      </div>
    </Link>
  );
}
